<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import { AppSvgIcon, AppRawButton } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

interface PeekPriceButtonProps {
  wide: boolean;
}

const props = defineProps<PeekPriceButtonProps>();

interface PeekPriceButtonEmits {
  (eventName: 'click'): void;
}

const emit = defineEmits<PeekPriceButtonEmits>();

const shouldUseAlternativeCopyForButton = computedAsync<boolean>(
  () => isExperimentAsync('peek_price_button'),
  false
);
</script>

<template>
  <AppRawButton
    class="peek-price-button"
    :class="{ wide: props.wide }"
    @click.stop="emit('click')"
  >
    <div class="icon-wrapper">
      <AppSvgIcon name="lowPriceCalendar" color="color-white" size="18" />
    </div>

    <div class="hotel-price-calendar-text">
      <span v-if="shouldUseAlternativeCopyForButton">
        {{ $t('planner.low_price_calendar.find_deal_label') }}
      </span>
      <span v-else>
        {{ $t('planner.low_price_calendar.label') }}
      </span>
      <span v-if="shouldUseAlternativeCopyForButton" class="cta-label">
        {{ $t('planner.low_price_calendar.show_calendar_label') }}
      </span>
      <span v-else class="cta-label">
        {{ $t('planner.low_price_calendar.action') }}
      </span>
    </div>
  </AppRawButton>
</template>

<style scoped lang="scss">
.peek-price-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  padding: var(--spacing-8);
  color: var(--color-neutral-70);
  background-color: var(--color-information-light);
  border-radius: var(--border-radius-8);
  border: 1px solid var(--color-brand-lilac);
  cursor: pointer;

  &.wide {
    width: 100%;
  }

  .icon-wrapper {
    background-color: var(--color-brand-violet);
    padding: var(--spacing-4);
    border-radius: var(--border-radius-8);
  }

  .hotel-price-calendar-text {
    display: flex;
    flex-direction: column;
    text-align: left;

    span {
      @include font-label-s-light;
      color: var(--color-neutral-70);
    }

    .cta-label {
      @include font-label-s-bold;
      text-decoration: underline;
    }
  }

  @include on-hover {
    background-color: var(--color-neutral-10);

    .cta-label {
      color: var(--color-brand-violet);
    }
  }
}
</style>
